import React from 'react';
import { Clock, DollarSign, AlertTriangle } from 'lucide-react';

/**
 * TimeStatsSummary component - Displays aggregated time statistics for a set of tasks
 * 
 * @param {Array} tasks - Array of tasks with time data
 * @param {Object} timeData - Map of task IDs to time summary data
 */
const TimeStatsSummary = ({ tasks = [], timeData = {} }) => {
  // Aggregate time data across all tasks
  let totalTrackedHours = 0;
  let totalEstimatedHours = 0;
  let billableHours = 0;
  let tasksWithTimers = 0;
  let tasksOverBudget = 0;
  let tasksWithActiveTimers = 0;
  
  // Process each task with time data
  tasks.forEach(task => {
    const taskTimeData = timeData[task._id];
    if (taskTimeData) {
      totalTrackedHours += taskTimeData.totalHours || 0;
      billableHours += taskTimeData.billableHours || 0;
      tasksWithTimers++;
      
      // Check if the task has an estimated time
      if (task.estimated_hours) {
        totalEstimatedHours += parseFloat(task.estimated_hours);
        
        // Check if task is over budget
        if ((taskTimeData.totalHours || 0) > parseFloat(task.estimated_hours)) {
          tasksOverBudget++;
        }
      }
      
      // Check if task has an active timer
      if (taskTimeData.activeEntries > 0) {
        tasksWithActiveTimers++;
      }
    }
  });
  
  // If no time data, return nothing
  if (tasksWithTimers === 0) {
    return null;
  }
  
  // Format number with 1 decimal place
  const formatNumber = (num) => {
    return parseFloat(num.toFixed(1));
  };
  
  // Calculate progress percentage
  const progressPercentage = totalEstimatedHours > 0 
    ? Math.min(100, Math.round((totalTrackedHours / totalEstimatedHours) * 100))
    : 0;
  
  return (
    <div className="bg-white dark:bg-darkbg-800 rounded-lg p-4 shadow-md mb-4">
      <div className="flex items-center mb-3">
        <Clock className="h-5 w-5 text-grayteck-600 dark:text-grayteck-400 mr-2" />
        <h3 className="text-sm font-medium text-grayteck-900 dark:text-grayteck-100">
          Time Summary
        </h3>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {/* Total Time */}
        <div className="bg-grayteck-50 dark:bg-darkbg-700 p-3 rounded-lg">
          <div className="flex items-center">
            <Clock className="w-4 h-4 text-grayteck-600 dark:text-grayteck-400 mr-2" />
            <span className="text-xs text-grayteck-600 dark:text-grayteck-400">
              Total Time
            </span>
          </div>
          <div className="mt-1 text-lg font-semibold text-grayteck-800 dark:text-grayteck-200">
            {formatNumber(totalTrackedHours)}h
            {totalEstimatedHours > 0 && (
              <span className="ml-1 text-sm font-normal text-grayteck-500 dark:text-grayteck-400">
                / {formatNumber(totalEstimatedHours)}h est.
              </span>
            )}
          </div>
          {totalEstimatedHours > 0 && (
            <div className="mt-1 w-full h-1.5 bg-grayteck-200 dark:bg-darkbg-600 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${
                  progressPercentage > 90 ? 'bg-red-500' : 
                  progressPercentage > 75 ? 'bg-yellow-500' : 
                  'bg-green-500'
                }`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          )}
        </div>
        
        {/* Billable Time */}
        <div className="bg-grayteck-50 dark:bg-darkbg-700 p-3 rounded-lg">
          <div className="flex items-center">
            <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
            <span className="text-xs text-grayteck-600 dark:text-grayteck-400">
              Billable Time
            </span>
          </div>
          <div className="mt-1 text-lg font-semibold text-green-600 dark:text-green-400">
            {formatNumber(billableHours)}h
            {totalTrackedHours > 0 && (
              <span className="ml-1 text-sm font-normal text-grayteck-500 dark:text-grayteck-400">
                ({Math.round((billableHours / totalTrackedHours) * 100)}%)
              </span>
            )}
          </div>
        </div>
        
        {/* Tasks with Time */}
        <div className="bg-grayteck-50 dark:bg-darkbg-700 p-3 rounded-lg">
          <div className="flex items-center">
            <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
            <span className="text-xs text-grayteck-600 dark:text-grayteck-400">
              Tasks with Time
            </span>
          </div>
          <div className="mt-1 text-lg font-semibold text-grayteck-800 dark:text-grayteck-200">
            {tasksWithTimers} 
            <span className="ml-1 text-sm font-normal text-grayteck-500 dark:text-grayteck-400">
              / {tasks.length} tasks
            </span>
          </div>
          {tasksWithActiveTimers > 0 && (
            <div className="mt-1 text-xs text-green-600 dark:text-green-400 flex items-center">
              <Clock className="w-3 h-3 mr-1 animate-pulse" />
              {tasksWithActiveTimers} active timer{tasksWithActiveTimers > 1 ? 's' : ''}
              
              {/* Show descriptions of active timers if any */}
              {tasks.some(task => {
                const taskTime = timeData[task._id];
                return taskTime && taskTime.activeEntries > 0 && taskTime.timeEntries?.some(e => e.status === 'running' && e.description);
              }) && (
                <span className="ml-2 text-xs text-grayteck-500 dark:text-grayteck-400">
                  (tracking: {tasks.map(task => {
                    const taskTime = timeData[task._id];
                    if (taskTime && taskTime.activeEntries > 0) {
                      const activeEntry = taskTime.timeEntries?.find(e => e.status === 'running');
                      if (activeEntry && activeEntry.description) {
                        return `"${activeEntry.description.slice(0, 30)}${activeEntry.description.length > 30 ? '...' : ''}"`;
                      }
                    }
                    return null;
                  }).filter(Boolean).join(', ')})
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Over Budget */}
        {totalEstimatedHours > 0 && (
          <div className="bg-grayteck-50 dark:bg-darkbg-700 p-3 rounded-lg">
            <div className="flex items-center">
              <AlertTriangle className={`w-4 h-4 ${
                tasksOverBudget > 0 ? 'text-red-600 dark:text-red-400' : 'text-grayteck-600 dark:text-grayteck-400'
              } mr-2`} />
              <span className="text-xs text-grayteck-600 dark:text-grayteck-400">
                Over Budget
              </span>
            </div>
            <div className={`mt-1 text-lg font-semibold ${
              tasksOverBudget > 0 ? 'text-red-600 dark:text-red-400' : 'text-grayteck-800 dark:text-grayteck-200'
            }`}>
              {tasksOverBudget}
              <span className="ml-1 text-sm font-normal text-grayteck-500 dark:text-grayteck-400">
                / {tasks.filter(t => t.estimated_hours).length} tasks
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeStatsSummary;